'use client';

import { useState, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';
import {
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Loader2,
  AlertTriangle,
  Upload,
  ExternalLink,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/shared/utils/formatting';
import { useUploadStore, type ActiveUpload } from '@/shared/stores/upload-store';
import {
  useProcessingPoller,
  type RefreshOutcome,
} from '@/features/upload/hooks/use-processing-poller';
import { useAbandonedUploadReporter } from '@/features/upload/hooks/use-abandoned-upload-reporter';
import { RESUMABLE_UPLOADS_ENABLED } from '@/features/upload/lib/resumable-uploads-flag';
import {
  deriveUploadState,
  MANUAL_REFRESH_COPY,
  POLL_LOST_COPY,
} from '@/features/upload/lib/upload-state';
import { UploadProgressBar } from './UploadProgressBar';

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Get phase display info
 */
function getPhaseInfo(upload: ActiveUpload): {
  label: string;
  icon: React.ReactNode;
  color: string;
} {
  // Stale (hydrated without live TUS client) — match the studio row's
  // Paused/Failed treatment so the indicator and the row tell the same story.
  if (upload.isStale) {
    const resumable = RESUMABLE_UPLOADS_ENABLED && !!upload.fileName && !!upload.fileSize;
    return {
      label: resumable ? 'Paused' : 'Failed',
      icon: <AlertTriangle className="w-4 h-4" />,
      color: 'text-red-400',
    };
  }

  switch (upload.phase) {
    case 'preparing':
      return {
        label: 'Preparing...',
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        color: 'text-slate-400',
      };
    case 'uploading':
      return {
        label: `${upload.progress}%`,
        icon: <Upload className="w-4 h-4" />,
        color: 'text-indigo-400',
      };
    case 'processing':
      if (upload.pollLost) {
        return {
          label: POLL_LOST_COPY.title,
          icon: <WifiOff className="w-4 h-4" />,
          color: 'text-amber-400',
        };
      }
      return {
        label: 'Processing...',
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        color: 'text-violet-400',
      };
    case 'complete':
      return {
        label: 'Complete',
        icon: <Check className="w-4 h-4" />,
        color: 'text-emerald-400',
      };
    case 'error':
      return {
        label: 'Failed',
        icon: <AlertTriangle className="w-4 h-4" />,
        color: 'text-red-400',
      };
    default:
      return {
        label: 'Idle',
        icon: <Upload className="w-4 h-4" />,
        color: 'text-text-tertiary',
      };
  }
}

/**
 * Single upload item in the expanded list
 */
function UploadItem({
  upload,
  onRefresh,
}: {
  upload: ActiveUpload;
  onRefresh: (uploadId: string) => Promise<RefreshOutcome>;
}) {
  const router = useRouter();
  // Presentation only: the component emits intent and renders the outcome; the
  // request itself belongs to the poller that owns the rate budget.
  const [checkState, setCheckState] = useState<'idle' | 'pending' | 'throttled'>('idle');
  const pathname = usePathname();
  const removeUpload = useUploadStore((state) => state.removeUpload);

  const phaseInfo = getPhaseInfo(upload);
  const uploadState = deriveUploadState({ entry: upload });
  const failureMessage = uploadState.kind === 'failed' ? uploadState.message : null;
  const canRemove = upload.phase === 'complete' || upload.phase === 'error' || upload.isStale;
  const isOnStudioContent = pathname === '/studio/content';
  const canSeeInStudio = !!upload.videoUuid && !upload.isStale && !isOnStudioContent;
  const showViewInStudio = upload.isStale && !!upload.videoUuid && !isOnStudioContent;

  return (
    <div className="p-3 border-b border-white/10 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={cn('mt-0.5', phaseInfo.color)}>{phaseInfo.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary truncate" title={upload.fileName}>
            {upload.metadata.title || upload.fileName}
          </p>

          {/* Progress bar - only show for active uploads, not stale */}
          {(upload.phase === 'uploading' || upload.phase === 'preparing') && !upload.isStale && (
            <div className="mt-2">
              <UploadProgressBar
                phase={upload.phase}
                progress={upload.progress}
                isStale={upload.isStale}
                size="sm"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-text-tertiary">{phaseInfo.label}</span>
                <span className="text-xs text-text-tertiary">
                  {formatBytes(upload.bytesUploaded)} / {formatBytes(upload.bytesTotal)}
                </span>
              </div>
            </div>
          )}

          {/* Stale session message */}
          {upload.isStale && (
            <div className="mt-2 space-y-1.5">
              <p className="text-xs text-red-400">
                {RESUMABLE_UPLOADS_ENABLED && !!upload.fileName && !!upload.fileSize
                  ? `Re-select ${upload.fileName} to continue uploading.`
                  : 'Delete this video to upload again.'}
              </p>
              {showViewInStudio && (
                <button
                  onClick={() => router.push('/studio/content')}
                  className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  View in Studio
                </button>
              )}
            </div>
          )}

          {failureMessage && (
            <p className="text-xs text-red-400 mt-1 line-clamp-2">{failureMessage}</p>
          )}

          {/* Completed status */}
          {upload.phase === 'complete' && (
            <p className="text-xs text-emerald-400 mt-1">Video ready!</p>
          )}

          {/* Processing status */}
          {uploadState.kind === 'processing' && (
            <div className="mt-1 flex items-center gap-2">
              <p
                className={cn(
                  'text-xs',
                  uploadState.pollLost ? 'text-amber-400' : 'text-violet-400'
                )}
              >
                {uploadState.pollLost ? POLL_LOST_COPY.message : 'Processing video...'}
              </p>
              {/* Reads through to Bunny's live API instead of waiting on the
                  webhook-fed column. Hidden while the poll is lost — that
                  request would fail the same way. */}
              {!uploadState.pollLost && (
                <button
                  type="button"
                  disabled={checkState !== 'idle'}
                  onClick={async () => {
                    setCheckState('pending');
                    const outcome = await onRefresh(upload.id);
                    setCheckState(outcome.throttled ? 'throttled' : 'idle');
                  }}
                  className="text-xs text-white/50 underline underline-offset-2 transition-colors hover:text-white/80 disabled:no-underline disabled:opacity-60"
                >
                  {checkState === 'pending' && MANUAL_REFRESH_COPY.pending}
                  {checkState === 'throttled' && MANUAL_REFRESH_COPY.throttled}
                  {checkState === 'idle' && MANUAL_REFRESH_COPY.action}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* See in Studio button - for non-stale uploads with videoUuid */}
          {canSeeInStudio && (
            <button
              onClick={() => router.push('/studio/content')}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="See in Studio"
            >
              <ExternalLink className="w-4 h-4 text-text-secondary" />
            </button>
          )}

          {canRemove && (
            <button
              onClick={() => removeUpload(upload.id)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * GlobalUploadIndicator - Floating UI showing active uploads
 *
 * Features:
 * - Collapsed: pill showing upload count and overall progress
 * - Expanded: list with individual upload details, pause/resume
 * - Fixed position: bottom-right corner
 * - Auto-hides when no uploads
 */
export function GlobalUploadIndicator() {
  const { refreshStatus } = useProcessingPoller();
  useAbandonedUploadReporter();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasHydrated = useUploadStore((state) => state._hasHydrated);
  // Use useShallow to prevent re-renders when uploads Map reference changes but contents are same
  const uploads = useUploadStore(useShallow((state) => state.uploads));
  const clearCompleted = useUploadStore((state) => state.clearCompleted);

  // Get uploads as array, excluding idle - memoized to prevent unnecessary recalcs
  const activeUploads = useMemo(
    () => Array.from(uploads.values()).filter((u) => u.phase !== 'idle'),
    [uploads]
  );

  // Calculate overall progress
  const uploadingCount = activeUploads.filter(
    (u) => (u.phase === 'uploading' || u.phase === 'preparing') && !u.isStale
  ).length;

  const processingCount = activeUploads.filter(
    (u) => u.phase === 'processing' && !u.isStale
  ).length;
  const completedCount = activeUploads.filter((u) => u.phase === 'complete').length;
  const errorCount = activeUploads.filter((u) => u.phase === 'error' && !u.isStale).length;
  const staleCount = activeUploads.filter((u) => u.isStale).length;
  const stalePausedCount = activeUploads.filter(
    (u) => u.isStale && RESUMABLE_UPLOADS_ENABLED && !!u.fileName && !!u.fileSize
  ).length;
  const staleFailedCount = staleCount - stalePausedCount;

  // Calculate average progress of uploading items
  const uploadingItems = activeUploads.filter((u) => u.phase === 'uploading' && !u.isStale);
  const averageProgress =
    uploadingItems.length > 0
      ? Math.round(uploadingItems.reduce((sum, u) => sum + u.progress, 0) / uploadingItems.length)
      : 0;

  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Don't render if not hydrated or no uploads
  if (!hasHydrated || activeUploads.length === 0) {
    return null;
  }

  // Determine status text
  let statusText = '';
  if (uploadingCount > 0) {
    statusText = `${uploadingCount} uploading`;
  } else if (processingCount > 0) {
    statusText = `${processingCount} processing`;
  } else if (stalePausedCount > 0) {
    statusText = `${stalePausedCount} paused`;
  } else if (staleFailedCount > 0) {
    statusText = `${staleFailedCount} failed`;
  } else if (errorCount > 0) {
    statusText = `${errorCount} failed`;
  } else if (completedCount > 0) {
    statusText = `${completedCount} complete`;
  }

  // Determine overall color — stale rows (paused/failed) and errors all use
  // red to match the studio row treatment.
  let statusColor = 'bg-indigo-400';
  if (staleCount > 0 && uploadingCount === 0 && processingCount === 0 && errorCount === 0) {
    statusColor = 'bg-red-400';
  } else if (errorCount > 0 && uploadingCount === 0 && processingCount === 0) {
    statusColor = 'bg-red-400';
  } else if (
    completedCount > 0 &&
    uploadingCount === 0 &&
    processingCount === 0 &&
    errorCount === 0 &&
    staleCount === 0
  ) {
    statusColor = 'bg-emerald-400';
  } else if (processingCount > 0 && uploadingCount === 0) {
    statusColor = 'bg-violet-400';
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Expanded view */}
      {isExpanded && (
        <div className="mb-2 w-80 bg-surface rounded-lg shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-text-primary">Uploads</h3>
            <div className="flex items-center gap-2">
              {completedCount > 0 && (
                <button
                  onClick={clearCompleted}
                  className="text-xs text-text-tertiary hover:text-text-secondary transition-colors"
                >
                  Clear completed
                </button>
              )}
            </div>
          </div>

          {/* Upload list */}
          <div className="max-h-64 overflow-y-auto">
            {activeUploads.map((upload) => (
              <UploadItem key={upload.id} upload={upload} onRefresh={refreshStatus} />
            ))}
          </div>
        </div>
      )}

      {/* Collapsed pill */}
      <button
        onClick={toggleExpanded}
        className={cn(
          'flex w-80 items-center justify-between gap-3 px-4 py-2.5 rounded-lg shadow-lg',
          'bg-surface'
        )}
      >
        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <div className={cn('w-2.5 h-2.5 rounded-full', statusColor)}>
            {(uploadingCount > 0 || processingCount > 0) && (
              <span className="block w-full h-full rounded-full animate-ping opacity-75" />
            )}
          </div>
          <span className="text-sm text-text-primary font-medium">{statusText}</span>
        </div>

        {/* Progress bar (when uploading) */}
        {uploadingCount > 0 && (
          <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-400 rounded-full transition-all duration-300"
              style={{ width: `${averageProgress}%` }}
            />
          </div>
        )}

        {/* Expand/collapse icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronUp className="w-4 h-4 text-text-tertiary" />
        )}
      </button>
    </div>
  );
}
